import React, { useState, useEffect, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { Calendar } from 'lucide-react';

/**
 * Custom Date Input component with forced yyyy/mm/dd format
 * Displays and accepts dates only in yyyy/mm/dd format
 */
export function DateInput({ value, onChange, className = '', placeholder = 'гггг/мм/дд', ...props }) {
  const [displayValue, setDisplayValue] = useState('');
  const [showCalendar, setShowCalendar] = useState(false);
  const dateInputRef = useRef(null);

  useEffect(() => {
    // Convert ISO date to yyyy/mm/dd display format WITHOUT timezone conversion
    if (value) {
      try {
        // Parse date string directly without timezone conversion
        if (value.includes('-')) {
          // Format: yyyy-mm-dd
          const [year, month, day] = value.split('T')[0].split('-');
          setDisplayValue(`${year}/${month}/${day}`);
        } else if (value.includes('/')) {
          // Already in correct format
          setDisplayValue(value);
        } else {
          setDisplayValue('');
        }
      } catch (e) {
        setDisplayValue('');
      }
    } else {
      setDisplayValue('');
    }
  }, [value]);

  const handleChange = (e) => {
    let input = e.target.value;
    
    // Remove any non-digit and non-slash characters
    input = input.replace(/[^\d/]/g, '');
    
    // Auto-add slashes
    if (input.length === 4 && !input.includes('/')) {
      input = input + '/';
    } else if (input.length === 7 && input.split('/').length === 2) {
      input = input + '/';
    }
    
    // Limit to yyyy/mm/dd format
    const parts = input.split('/');
    if (parts[0]?.length > 4) {
      parts[0] = parts[0].slice(0, 4);
    }
    if (parts[1]?.length > 2) {
      parts[1] = parts[1].slice(0, 2);
    }
    if (parts[2]?.length > 2) {
      parts[2] = parts[2].slice(0, 2);
    }
    
    input = parts.join('/');
    
    // Max length
    if (input.length > 10) {
      input = input.slice(0, 10);
    }
    
    setDisplayValue(input);
    
    // Try to parse and convert to ISO format for parent component
    if (input.length === 10) {
      const [year, month, day] = input.split('/');
      const yearNum = parseInt(year, 10);
      const monthNum = parseInt(month, 10);
      const dayNum = parseInt(day, 10);
      
      // Validate
      if (
        yearNum >= 1900 && yearNum <= 2100 &&
        monthNum >= 1 && monthNum <= 12 &&
        dayNum >= 1 && dayNum <= 31
      ) {
        try {
          // Create date string in yyyy-mm-dd format WITHOUT timezone conversion
          const isoDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
          onChange({ target: { value: isoDate } });
          return;
        } catch (e) {
          // Invalid date
        }
      }
    }
    
    // If not valid complete date, still update display but send empty to parent
    if (input.length < 10) {
      onChange({ target: { value: '' } });
    }
  };

  const handleBlur = () => {
    // Validate on blur
    if (displayValue && displayValue.length === 10) {
      const [year, month, day] = displayValue.split('/');
      const yearNum = parseInt(year, 10);
      const monthNum = parseInt(month, 10);
      const dayNum = parseInt(day, 10);
      
      // Simple validation without timezone conversion
      if (
        yearNum < 1900 || yearNum > 2100 ||
        monthNum < 1 || monthNum > 12 ||
        dayNum < 1 || dayNum > 31
      ) {
        setDisplayValue('');
        onChange({ target: { value: '' } });
      }
    } else if (displayValue && displayValue.length > 0 && displayValue.length < 10) {
      // Incomplete date - clear it
      setDisplayValue('');
      onChange({ target: { value: '' } });
    }
  };

  const handleNativeDateChange = (e) => {
    // When native calendar is used, convert to our format
    const isoDate = e.target.value;
    if (isoDate) {
      onChange({ target: { value: isoDate } });
    }
  };

  return (
    <div className="relative">
      <Input
        type="text"
        value={displayValue}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        className={`pr-10 ${className}`}
        {...props}
      />
      {/* Hidden native date input for calendar picker - covering entire right side */}
      <input
        ref={dateInputRef}
        type="date"
        value={value || ''}
        onChange={handleNativeDateChange}
        className="absolute right-0 top-0 w-12 h-full opacity-0 cursor-pointer z-10"
        title="Открыть календарь"
        style={{ pointerEvents: 'auto' }}
      />
      <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none z-0" />
    </div>
  );
}
